import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  DollarSign, CheckCircle, Clock, Loader2, AlertTriangle, Search, Users,
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (v) => Number(v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 });

export default function PartnerPayrollPage() {
  const { isAdmin, isInternal } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [summary, setSummary] = useState({});
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [partnerId, setPartnerId] = useState('all_partners');
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: tab };
      if (partnerId && partnerId !== 'all_partners') params.partner_id = partnerId;
      if (search) params.search = search;
      const res = await api.get('/partner/payroll-queue', { params });
      setQueue(res.data.commissions || []);
      setSummary(res.data.summary || {});
    } catch (err) {
      toast.error('Failed to load payroll queue');
    } finally { setLoading(false); }
  }, [tab, partnerId, search]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    api.get('/partner/list').then(r => setPartners(r.data || [])).catch(() => {});
  }, []);

  const updateStatus = async (_id, _status, _notes) => {
    // v3.15-1217 PAY-MOD-001 — Partner commission mutation retired.
    toast.error('This workflow has been retired. Use Financial Lifecycle to record stage transitions.');
  };

  const batchUpdate = async (_status) => {
    // v3.15-1217 PAY-MOD-001 — Partner commission batch mutation retired.
    toast.error('This workflow has been retired. Use Financial Lifecycle to record stage transitions.');
  };

  if (!isInternal()) return <div className="flex items-center justify-center h-64"><p className="text-slate-500">Internal access required</p></div>;

  const tabs = [
    { key: 'pending', label: 'Pending', count: summary.pending_count, color: 'text-amber-600' },
    { key: 'approved', label: 'Approved', count: summary.approved_count, color: 'text-blue-600' },
    { key: 'paid', label: 'Paid', count: summary.paid_count, color: 'text-green-600' },
    { key: 'all', label: 'All', count: null, color: 'text-slate-600' },
  ];

  return (
    <div className="space-y-4" data-testid="partner-payroll-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-primary">Partner Payroll</h1>
          <p className="text-sm text-slate-500">Marketing partner commission approval queue</p>
        </div>
        {isAdmin() && (
          <Button
            size="sm"
            variant="outline"
            disabled
            title="Retired in v3.15-1217 — use /api/payroll-engine/replay-projection"
            data-testid="partner-recalc-btn-retired"
          >
            Recalc (retired)
          </Button>
        )}
      </div>

      {/* v3.15-1217 PAY-MOD-001 — Retirement banner. Partner Payroll
          mutations (approve / deny / mark paid / batch / recalc) are
          disabled. Historical viewing remains available. */}
      <div
        className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2"
        data-testid="partner-payroll-retired-banner"
      >
        <span className="inline-flex h-2 w-2 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
        <div>
          <div className="font-semibold">Legacy partner-payroll mutations are retired (v3.15-1217).</div>
          <div className="mt-0.5 leading-relaxed">
            Partner commission approvals and payment status can no longer be recorded here — every stage
            transition now flows through the immutable Financial Lifecycle (MM-002).
            Historical rows remain visible for audit. New partner earnings must originate from a Compensation Plan.
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-0 border rounded-lg overflow-hidden" data-testid="partner-payroll-summary">
        <div className="p-3 border-r bg-amber-50 text-center">
          <p className="text-[10px] text-amber-600 uppercase font-semibold">Pending</p>
          <p className="text-xl font-bold text-amber-700">{fmt(summary.pending)}</p>
          <p className="text-[10px] text-amber-500">{summary.pending_count || 0} deals</p>
        </div>
        <div className="p-3 border-r bg-blue-50 text-center">
          <p className="text-[10px] text-blue-600 uppercase font-semibold">Approved</p>
          <p className="text-xl font-bold text-blue-700">{fmt(summary.approved)}</p>
          <p className="text-[10px] text-blue-500">{summary.approved_count || 0} deals</p>
        </div>
        <div className="p-3 bg-green-50 text-center">
          <p className="text-[10px] text-green-600 uppercase font-semibold">Paid</p>
          <p className="text-xl font-bold text-green-700">{fmt(summary.paid)}</p>
          <p className="text-[10px] text-green-500">{summary.paid_count || 0} deals</p>
        </div>
      </div>

      {/* Tabs + Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {tabs.map(t => (
          <Button key={t.key} size="sm" variant={tab === t.key ? 'default' : 'outline'}
            className={`h-7 text-xs ${tab === t.key ? '' : t.color}`}
            onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}>
            {t.label}{t.count != null ? ` (${t.count})` : ''}
          </Button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <Select value={partnerId} onValueChange={setPartnerId}>
            <SelectTrigger className="w-[180px] h-7 text-xs"><SelectValue placeholder="All Partners" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all_partners">All Partners</SelectItem>
              {partners.map(p => <SelectItem key={p.user_id} value={p.user_id}>{p.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="absolute left-2 top-1.5 h-3.5 w-3.5 text-slate-400" />
            <Input className="h-7 text-xs pl-7 w-[150px]" placeholder="Search..." value={search}
              onChange={e => setSearch(e.target.value)} data-testid="partner-search" />
          </div>
        </div>
      </div>

      {/* Batch actions — v3.15-1217 PAY-MOD-001: retired (kept in place, disabled) */}
      {queue.length > 0 && tab === 'pending' && isAdmin() && (
        <div className="flex gap-2">
          <Button size="sm" className="h-6 text-[10px]" disabled title="Retired — use Financial Lifecycle" data-testid="partner-batch-approve-retired">Approve All (retired)</Button>
          <Button size="sm" variant="outline" className="h-6 text-[10px]" disabled title="Retired — use Financial Lifecycle" data-testid="partner-batch-deny-retired">Deny All (retired)</Button>
        </div>
      )}
      {queue.length > 0 && tab === 'approved' && isAdmin() && (
        <Button size="sm" className="h-6 text-[10px]" disabled title="Retired — use Financial Lifecycle" data-testid="partner-batch-pay-retired">Mark All Paid (retired)</Button>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : queue.length === 0 ? (
            <div className="text-center py-16">
              <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No {tab} commissions</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs">Partner</TableHead>
                  <TableHead className="text-xs">Client</TableHead>
                  <TableHead className="text-xs">Type</TableHead>
                  <TableHead className="text-xs text-right">Deal Amt</TableHead>
                  <TableHead className="text-xs text-right">Commission</TableHead>
                  <TableHead className="text-xs">Date</TableHead>
                  <TableHead className="text-xs text-center">Status</TableHead>
                  {tab === 'paid' && <TableHead className="text-xs">Paid Date</TableHead>}
                  <TableHead className="text-xs">Notes</TableHead>
                  {(tab === 'pending' || tab === 'approved') && <TableHead className="text-xs text-center">Action</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {queue.map(c => (
                  <TableRow key={c.id} data-testid={`partner-comm-${c.id}`}>
                    <TableCell className="text-xs font-medium">{c.partner_name}</TableCell>
                    <TableCell>
                      <span className="text-xs cursor-pointer hover:text-blue-600 hover:underline"
                        onClick={() => c.client_id && navigate(`/clients/${c.client_id}`)}>
                        {c.client_name || '-'}
                      </span>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{(c.commission_type || '').replace(/_/g, ' ')}</Badge></TableCell>
                    <TableCell className="text-xs text-right font-mono">{c.deal_amount ? fmt(c.deal_amount) : '-'}</TableCell>
                    <TableCell className="text-right font-semibold text-emerald-600 font-mono">{fmt(c.amount)}</TableCell>
                    <TableCell className="text-xs text-slate-500">{(c.created_at || '').slice(0, 10)}</TableCell>
                    <TableCell className="text-center">
                      <Badge className={`text-[10px] ${
                        c.status === 'paid' ? 'bg-green-100 text-green-700' :
                        c.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                        c.status === 'denied' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>{c.status}</Badge>
                    </TableCell>
                    {tab === 'paid' && <TableCell className="text-xs text-slate-500">{c.paid_date || '-'}</TableCell>}
                    <TableCell className="text-xs text-slate-500 max-w-[120px] truncate">{c.notes || '-'}</TableCell>
                    {tab === 'pending' && (
                      <TableCell className="text-center">
                        {/* v3.15-1217 PAY-MOD-001 — Row-level partner commission mutations retired. */}
                        <span className="text-[9px] italic text-slate-400" data-testid={`partner-row-actions-retired-${c.id}`}>
                          retired
                        </span>
                      </TableCell>
                    )}
                    {tab === 'approved' && (
                      <TableCell className="text-center">
                        <span className="text-[9px] italic text-slate-400" data-testid={`partner-row-pay-retired-${c.id}`}>
                          retired
                        </span>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
